import { createContext, useCallback, useContext, RefObject } from 'react'

const SCROLL_TIMEOUT = 500

export const ContentScrollContext =
  createContext<RefObject<HTMLDivElement | null> | null>(null)

export const useContentScroll = (): {
  scrollTo: (options?: ScrollToOptions) => void
  scrollToElement: (element: HTMLElement | null) => Promise<void>
} => {
  const contentRef = useContext(ContentScrollContext)

  const scrollTo = useCallback(
    (options?: ScrollToOptions) => {
      contentRef?.current?.scrollTo(options)
    },
    [contentRef],
  )

  const scrollToElement = useCallback(
    (element: HTMLElement | null): Promise<void> =>
      new Promise((resolve) => {
        if (!contentRef?.current || !element) {
          resolve()
          return
        }

        const contentRect = contentRef.current.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const targetTop =
          elementRect.top - contentRect.top + contentRef.current.scrollTop

        if (Math.abs(contentRef.current.scrollTop - targetTop) < 1) {
          resolve()
          return
        }

        const timeout = setTimeout(resolve, SCROLL_TIMEOUT)

        contentRef.current.addEventListener(
          'scrollend',
          () => {
            clearTimeout(timeout)
            resolve()
          },
          { once: true },
        )

        contentRef.current.scrollTo({
          top: targetTop,
          behavior: 'smooth',
        })
      }),
    [contentRef],
  )

  return { scrollTo, scrollToElement }
}
